import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "./UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  ChevronRight,
  Mail,
  Send,
  Download,
  ArrowLeft,
  Paperclip,
  Search,
  RefreshCw,
  Loader2,
  User,
  Clock,
  FileText,
  Reply,
  ReplyAll,
  CornerDownRight,
  Settings,
  CheckCircle2
} from "lucide-react";
import ComposeEmail from "./ComposeEmail";
import ClassifyAttachmentDialog from "./ClassifyAttachmentDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function EmailThread({ thread, onRefresh, projectId, approvedFiles, userMailbox, canClassify }) {
  const [expanded, setExpanded] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyType, setReplyType] = useState("reply_all");
  const [replyBody, setReplyBody] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [classifyTarget, setClassifyTarget] = useState(null);

  const emails = thread.emails || [];
  const latestEmail = emails[0];
  const olderEmails = emails.slice(1);
  
  // Check if user can reply (has mailbox)
  const canReply = userMailbox?.has_mailbox;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return date.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: 'short' });
      } else {
        return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", month: 'short', day: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  const getReplyRecipients = () => {
    if (!latestEmail) return { to: [], cc: [] };
    
    const fromEmail = latestEmail.from_address?.email || "";
    const toEmails = latestEmail.to_addresses?.map(a => a.email) || [];
    const ccEmails = latestEmail.cc_addresses?.map(a => a.email) || [];
    
    if (replyType === "reply") {
      // Reply to sender only (or recipients if we sent it)
      if (latestEmail.direction === "sent") {
        return { to: toEmails.slice(0, 1), cc: [] };
      }
      return { to: [fromEmail], cc: [] };
    } else {
      // Reply All
      const all = new Set([fromEmail, ...toEmails, ...ccEmails]);
      const allList = Array.from(all).filter(e => e);
      return { to: allList.slice(0, 1), cc: allList.slice(1) };
    }
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      toast.error("Please enter a reply message");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(
        `${API}/email/reply`,
        {
          thread_id: thread.thread_id,
          in_reply_to_email_id: latestEmail.id,
          reply_type: replyType,
          body: replyBody,
          attachment_file_ids: selectedAttachments
        },
        getAuthHeaders()
      );
      toast.success("Reply sent successfully");
      setReplyBody("");
      setSelectedAttachments([]);
      setShowReply(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const toggleAttachment = (fileId) => {
    setSelectedAttachments(prev => 
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const renderEmailMessage = (email, isLatest = false) => {
    const isSent = email.direction === "sent";
    
    return (
      <div 
        key={email.id}
        className={`p-4 rounded-lg border ${isLatest ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'} ${isSent ? 'border-l-4 border-l-blue-400' : 'border-l-4 border-l-green-400'}`}
        data-testid={`email-message-${email.id}`}
      >
        {/* Email Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isSent ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
              {isSent ? <Send className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded ${isSent ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                  {isSent ? 'SENT' : 'RECEIVED'}
                </span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(email.email_date)}
                </span>
              </div>
              <div className="text-xs text-gray-600 space-y-0.5">
                <div><span className="font-medium text-gray-700">From:</span> {email.from_address?.email || ''}{email.from_address?.name ? ` (${email.from_address.name})` : ''}</div>
                <div><span className="font-medium text-gray-700">To:</span> {email.to_addresses?.map(a => a.email).join(', ') || ''}</div>
                {email.cc_addresses?.length > 0 && (
                  <div><span className="font-medium text-gray-700">CC:</span> {email.cc_addresses.map(a => a.email).join(', ')}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Email Body */}
        <div className="text-sm text-gray-700 whitespace-pre-wrap pl-11">
          {email.body_text || "(No content)"}
        </div>

        {/* Attachments */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="mt-3 pl-11">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <Paperclip className="w-3 h-3" />
              {email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}
            </div>
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((att, idx) => {
                const isPending = att.status === "pending" || (!att.status && !att.linked_file_id && email.direction === "received");
                const isClassified = att.status === "classified" || att.linked_file_id;
                const isDiscarded = att.status === "discarded";
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      isClassified ? "bg-green-50 border border-green-200" :
                      isDiscarded ? "bg-gray-100 border border-gray-300 line-through opacity-60" :
                      isPending ? "bg-amber-50 border border-amber-200" : "bg-gray-100"
                    }`}
                    data-testid={`attachment-chip-${email.id}-${idx}`}
                  >
                    <FileText className="w-3 h-3 text-gray-400" />
                    <span className="truncate max-w-[150px]" title={att.filename}>{att.filename}</span>
                    {isClassified && (
                      <span className="text-green-700 ml-1">✓ Linked</span>
                    )}
                    {isDiscarded && (
                      <span className="text-gray-500 ml-1">discarded</span>
                    )}
                    {isPending && canClassify && att.r2_key && (
                      <button
                        className="ml-2 px-2 py-0.5 bg-amber-600 text-white rounded hover:bg-amber-700"
                        onClick={() => setClassifyTarget({
                          email_id: email.id,
                          attachment_index: idx,
                          filename: att.filename,
                          content_type: att.content_type,
                          size: att.size,
                          project_id: email.project_id || projectId,
                        })}
                        data-testid={`classify-btn-${email.id}-${idx}`}
                      >
                        Classify
                      </button>
                    )}
                    {isPending && !att.r2_key && (
                      <span className="ml-2 text-amber-700 italic">re-sync to fetch</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      data-testid={`email-thread-${thread.thread_id}`}
    >
      {/* Thread Header */}
      <div 
        className="p-4 bg-gradient-to-r from-gray-50 to-white cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-gray-400">
              {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-500" />
                {thread.subject || "(No Subject)"}
              </h3>
              <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                {latestEmail && (
                  <>
                    <div><span className="font-medium text-gray-600">From:</span> {latestEmail.from_address?.email || ''}</div>
                    <div><span className="font-medium text-gray-600">To:</span> {latestEmail.to_addresses?.map(a => a.email).join(', ') || ''}</div>
                    {latestEmail.cc_addresses?.length > 0 && (
                      <div><span className="font-medium text-gray-600">CC:</span> {latestEmail.cc_addresses.map(a => a.email).join(', ')}</div>
                    )}
                  </>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="bg-gray-200 px-2 py-0.5 rounded-full">
                    {thread.message_count} message{thread.message_count > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400">
            {formatDate(thread.latest_message_at)}
          </div>
        </div>
      </div>

      {/* Thread Messages */}
      {expanded && (
        <div className="border-t border-gray-100">
          {/* Latest Message (Always Visible) */}
          {latestEmail && (
            <div className="p-4">
              {renderEmailMessage(latestEmail, true)}
            </div>
          )}

          {/* Older Messages (Collapsible) */}
          {olderEmails.length > 0 && (
            <div className="border-t border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllMessages(!showAllMessages);
                }}
                className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2 transition-colors"
                data-testid={`toggle-older-messages-${thread.thread_id}`}
              >
                {showAllMessages ? (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Hide {olderEmails.length} earlier message{olderEmails.length > 1 ? 's' : ''}
                  </>
                ) : (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    Show {olderEmails.length} earlier message{olderEmails.length > 1 ? 's' : ''}
                  </>
                )}
              </button>

              {showAllMessages && (
                <div className="p-4 space-y-3 bg-gray-50">
                  {olderEmails.map((email) => renderEmailMessage(email, false))}
                </div>
              )}
            </div>
          )}

          {/* Quick Reply Section */}
          {latestEmail && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              {!canReply ? (
                <div className="text-center text-sm text-gray-500 py-2">
                  <p>No mailbox configured for your account. Contact an administrator to enable email reply.</p>
                </div>
              ) : !showReply ? (
                <Button
                  variant="outline"
                  onClick={() => setShowReply(true)}
                  className="w-full flex items-center justify-center gap-2"
                  data-testid={`quick-reply-btn-${thread.thread_id}`}
                >
                  <CornerDownRight className="w-4 h-4" />
                  Quick Reply {userMailbox?.mailbox?.email && `(from ${userMailbox.mailbox.email})`}
                </Button>
              ) : (
                <div className="space-y-3" data-testid={`reply-form-${thread.thread_id}`}>
                  {/* Sending As Info */}
                  <div className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded">
                    Sending from: {userMailbox?.mailbox?.email}
                  </div>
                  
                  {/* Reply Type Selector */}
                  <div className="flex items-center gap-3">
                    <Select value={replyType} onValueChange={setReplyType}>
                      <SelectTrigger className="w-40" data-testid="reply-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reply">
                          <div className="flex items-center gap-2">
                            <Reply className="w-4 h-4" />
                            Reply
                          </div>
                        </SelectItem>
                        <SelectItem value="reply_all">
                          <div className="flex items-center gap-2">
                            <ReplyAll className="w-4 h-4" />
                            Reply All
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-sm text-gray-500">
                      To: {getReplyRecipients().to.join(', ')}
                      {getReplyRecipients().cc.length > 0 && (
                        <span className="ml-2">CC: {getReplyRecipients().cc.join(', ')}</span>
                      )}
                    </div>
                  </div>

                  {/* Reply Body */}
                  <Textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    className="bg-white"
                    data-testid="reply-body-input"
                  />

                  {/* Attachments (Approved Files) */}
                  {approvedFiles && approvedFiles.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Paperclip className="w-4 h-4" />
                        Attach approved files:
                      </div>
                      <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                        {approvedFiles.map((file) => (
                          <button
                            key={file.id}
                            type="button"
                            onClick={() => toggleAttachment(file.id)}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${
                              selectedAttachments.includes(file.id)
                                ? 'bg-blue-50 border-blue-400 text-blue-700'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                            }`}
                          >
                            {selectedAttachments.includes(file.id) 
                              ? <CheckCircle2 className="w-3 h-3 text-green-600 shrink-0" />
                              : <FileText className="w-3 h-3 shrink-0" />
                            }
                            <span className="truncate max-w-[120px]">{file.file_name}</span>
                            {file.file_size > 0 && (
                              <span className="text-gray-400 shrink-0">({formatFileSize(file.file_size)})</span>
                            )}
                          </button>
                        ))}
                      </div>
                      {selectedAttachments.length > 0 && (() => {
                        const totalSize = selectedAttachments.reduce((sum, fid) => {
                          const f = approvedFiles.find(x => x.id === fid);
                          return sum + (f?.file_size || 0);
                        }, 0);
                        return totalSize > 0 ? (
                          <p className="text-xs text-gray-500 mt-1.5" data-testid="reply-total-attachment-size">
                            Total: <span className="font-medium text-gray-700">{formatFileSize(totalSize)}</span>
                            {totalSize > 20 * 1024 * 1024 && (
                              <span className="text-amber-600 ml-2">Large — delivery may be affected</span>
                            )}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSendReply}
                      disabled={sending || !replyBody.trim()}
                      data-testid="send-reply-btn"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Reply
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowReply(false);
                        setReplyBody("");
                        setSelectedAttachments([]);
                      }}
                    >
                      Cancel
                    </Button>
                    {selectedAttachments.length > 0 && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        {selectedAttachments.length} file{selectedAttachments.length > 1 ? 's' : ''} attached
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Classify Attachment Dialog */}
      <ClassifyAttachmentDialog
        open={!!classifyTarget}
        target={classifyTarget}
        onClose={() => setClassifyTarget(null)}
        onClassified={onRefresh}
      />
    </div>
  );
}

export default function ProjectEmails({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [versions, setVersions] = useState([]);
  const [approvedFiles, setApprovedFiles] = useState([]);
  const [userMailbox, setUserMailbox] = useState(null);
  const [canClassify, setCanClassify] = useState(false);
  
  // Sync settings state
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [syncSettings, setSyncSettings] = useState({ auto_sync_enabled: true, sync_interval_minutes: 30 });
  const [savingSyncSettings, setSavingSyncSettings] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSyncSettings();
    fetchClassifyPerm();
  }, [projectId]);

  const fetchClassifyPerm = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      const perms = res.data.effective_permissions || [];
      setCanClassify(perms.includes("email.attachments.classify") || user?.role === "ADMIN");
    } catch {
      setCanClassify(user?.role === "ADMIN");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [emailsRes, projectRes, versionsRes, mailboxRes] = await Promise.all([
        axios.get(`${API}/email/project/${projectId}`, getAuthHeaders()),
        axios.get(`${API}/projects/${projectId}`, getAuthHeaders()),
        axios.get(`${API}/projects/${projectId}/versions`, getAuthHeaders()),
        axios.get(`${API}/email/my-mailbox`, getAuthHeaders()).catch(() => ({ data: { has_mailbox: false } }))
      ]);
      
      setThreads(emailsRes.data.threads || []);
      setProject(projectRes.data);
      setVersions(versionsRes.data || []);
      setUserMailbox(mailboxRes.data);
      
      // Extract approved files for attachment
      const approved = [];
      (versionsRes.data || []).forEach(v => {
        (v.files || []).forEach(f => {
          if (f.status === "Approved") {
            approved.push({ ...f, doc_type: v.doc_type, version: v.version });
          }
        });
      });
      setApprovedFiles(approved);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-sync`, getAuthHeaders());
      setSyncSettings(res.data);
    } catch (error) {
      // Use defaults if can't fetch
    }
  };

  // Admin sync - syncs all mailboxes
  const handleAdminSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/email/sync`, {}, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Synced ${response.data.synced_count} emails, ${response.data.classified_count} classified`);
        fetchData(); // Refresh
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // User sync - syncs only their mailbox
  const handleUserSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/email/sync-my-mailbox`, {}, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Synced ${response.data.synced_count} emails, ${response.data.classified_count} classified`);
        fetchData(); // Refresh to get updated last_sync_at
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSyncSettings = async () => {
    setSavingSyncSettings(true);
    try {
      const res = await axios.put(`${API}/settings/email-sync`, syncSettings, getAuthHeaders());
      setSyncSettings(res.data);
      toast.success("Sync settings updated");
      setShowSyncSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSyncSettings(false);
    }
  };

  // Format relative time for last sync
  const formatLastSync = (isoString) => {
    if (!isoString) return "Never";
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
      return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
    } catch {
      return "Unknown";
    }
  };

  const filteredThreads = threads.filter(thread => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      thread.subject?.toLowerCase().includes(query) ||
      thread.participants?.some(p => p.toLowerCase().includes(query))
    );
  });

  // Check if user can send email (has role + has mailbox)
  const hasEmailRole = user?.role && ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role);
  const canSendEmail = hasEmailRole && userMailbox?.has_mailbox;
  
  // Check if user is Super Admin (can change sync settings)
  const isSuperAdmin = user?.role === "ADMIN" || user?.role_ids?.includes("role-super-admin");

  return (
    <div data-testid="project-emails-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="w-6 h-6" />
              Project Emails
            </h1>
            <p className="text-blue-100 text-sm mt-1">
              {project?.name || "Loading..."} ({projectId})
              {userMailbox?.has_mailbox && (
                <span className="ml-3 text-blue-200">
                  • Sending as: {userMailbox.mailbox?.email}
                  {userMailbox.mailbox?.last_sync_at && (
                    <span className="ml-2">• Last sync: {formatLastSync(userMailbox.mailbox.last_sync_at)}</span>
                  )}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            {canSendEmail ? (
              <Button
                onClick={() => setShowCompose(true)}
                className="bg-white text-blue-600 hover:bg-blue-50"
                data-testid="compose-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Compose
              </Button>
            ) : hasEmailRole && !userMailbox?.has_mailbox ? (
              <Button
                variant="outline"
                className="bg-white/10 text-white border-white/30 opacity-75 cursor-not-allowed"
                disabled
                title="No mailbox configured for your account"
                data-testid="compose-email-btn-disabled"
              >
                <Send className="w-4 h-4 mr-2" />
                Compose (No Mailbox)
              </Button>
            ) : null}
            
            {/* Sync button for users with mailbox */}
            {userMailbox?.has_mailbox && (
              <Button
                onClick={handleUserSync}
                disabled={syncing}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                data-testid="sync-my-emails-btn"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sync My Mailbox
              </Button>
            )}
            
            {/* Admin-only: Sync All + Settings */}
            {isSuperAdmin && (
              <>
                <Button
                  onClick={handleAdminSync}
                  disabled={syncing}
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                  data-testid="sync-all-emails-btn"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync All
                </Button>
                <Button
                  onClick={() => setShowSyncSettings(true)}
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                  data-testid="sync-settings-btn"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </>
            )}
            
            <Button
              onClick={() => navigate(`/project/${projectId}`)}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              data-testid="back-to-project-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Project
            </Button>
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="erp-container">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search emails by subject or participant..."
              className="pl-10"
              data-testid="email-search-input"
            />
          </div>
        </div>

        {/* Email Threads */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-500">Loading emails...</span>
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Mail className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {searchQuery ? "No matching emails" : "No emails yet"}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery 
                ? "Try adjusting your search query" 
                : "Send an email or sync from your inbox to see emails here"}
            </p>
            {canSendEmail && !searchQuery && (
              <Button onClick={() => setShowCompose(true)} className="bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Compose First Email
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 mb-2">
              {filteredThreads.length} thread{filteredThreads.length !== 1 ? 's' : ''} 
              • {threads.reduce((sum, t) => sum + t.message_count, 0)} total messages
            </div>
            {filteredThreads.map((thread) => (
              <EmailThread 
                key={thread.thread_id} 
                thread={thread}
                onRefresh={fetchData}
                projectId={projectId}
                approvedFiles={approvedFiles}
                userMailbox={userMailbox}
                canClassify={canClassify}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compose Email Dialog */}
      <ComposeEmail
        open={showCompose}
        onOpenChange={setShowCompose}
        projectId={projectId}
        projectName={project?.name || ""}
        availableFiles={versions.flatMap(v => v.files || [])}
        onEmailSent={() => {
          fetchData();
          toast.success("Email sent!");
        }}
        userMailbox={userMailbox}
      />

      {/* Sync Settings Dialog (Super Admin only) */}
      <Dialog open={showSyncSettings} onOpenChange={setShowSyncSettings}>
        <DialogContent className="sm:max-w-md" data-testid="sync-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Email Auto-Sync Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Auto-sync toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="auto-sync" className="text-base font-medium">Auto-Sync Emails</Label>
                <p className="text-sm text-gray-500">Automatically sync emails from all mailboxes</p>
              </div>
              <Switch
                id="auto-sync"
                checked={syncSettings.auto_sync_enabled}
                onCheckedChange={(checked) => setSyncSettings(prev => ({ ...prev, auto_sync_enabled: checked }))}
                data-testid="auto-sync-toggle"
              />
            </div>
            
            {/* Sync interval */}
            <div className="space-y-2">
              <Label htmlFor="sync-interval">Sync Interval</Label>
              <Select
                value={String(syncSettings.sync_interval_minutes)}
                onValueChange={(val) => setSyncSettings(prev => ({ ...prev, sync_interval_minutes: parseInt(val) }))}
                disabled={!syncSettings.auto_sync_enabled}
              >
                <SelectTrigger id="sync-interval" data-testid="sync-interval-select">
                  <SelectValue placeholder="Select interval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Every 5 minutes</SelectItem>
                  <SelectItem value="10">Every 10 minutes</SelectItem>
                  <SelectItem value="15">Every 15 minutes</SelectItem>
                  <SelectItem value="30">Every 30 minutes</SelectItem>
                  <SelectItem value="60">Every 1 hour</SelectItem>
                  <SelectItem value="120">Every 2 hours</SelectItem>
                  <SelectItem value="360">Every 6 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {syncSettings.auto_sync_enabled 
                  ? `Emails will be synced every ${syncSettings.sync_interval_minutes} minutes` 
                  : "Auto-sync is disabled"}
              </p>
            </div>

            {/* Last auto-sync info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${syncSettings.auto_sync_enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <span className="font-medium text-gray-700">
                  {syncSettings.auto_sync_enabled ? 'Background sync active' : 'Background sync disabled'}
                </span>
              </div>
              {syncSettings.last_auto_sync_at && (
                <p className="text-gray-600 pl-4">
                  Last auto-sync: {formatLastSync(syncSettings.last_auto_sync_at)}
                </p>
              )}
              {syncSettings.auto_sync_enabled && syncSettings.last_auto_sync_at && (
                <p className="text-gray-500 pl-4 text-xs">
                  Next sync in ~{Math.max(0, syncSettings.sync_interval_minutes - Math.floor((Date.now() - new Date(syncSettings.last_auto_sync_at).getTime()) / 60000))} minutes
                </p>
              )}
              {syncSettings.updated_by && (
                <p className="text-gray-500 pl-4 text-xs">
                  Settings updated by: {syncSettings.updated_by}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSyncSettings(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSyncSettings}
              disabled={savingSyncSettings}
              data-testid="save-sync-settings-btn"
            >
              {savingSyncSettings ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Settings"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
